import React, { useRef } from 'react';
import { observer } from 'mobx-react';
import { AppVM } from './appVM';
import stores from '../../models/Stores';
import { BrowserRouter, Switch, Route, Redirect } from 'react-router-dom';
import { Tophat } from '../common/Tophat/Tophat';
import { TrainingComp } from '../common/Training/Training';
import { SideNav } from '../common/SideNav/SideNav';
import { Login } from '../common/Login/Login';
import { Error401 } from '../common/Error401/Error401';
import { Homepage } from '../common/Homepage/Homepage';
import { GISBox } from '../EFH2/GISBox/GISBox';
import { Metadata } from '../EFH2/Metadata';
import { Intro } from '../EFH2/Intro/Intro';
import { DesignContainer } from '../Design/DesignContainer/DesignContainer';
import { Breadcrumbs } from 'cd-eft/components/common/Breadcrumbs/Breadcrumbs';
import { WaterWaySizingCalculatorDetails } from '../EFH2/Waterway Sizing Calulator/Details';
import { WaterWayInputDetails } from '../Design/Waterway/WDTContainer/WDTContentComp/WaterwayInput/IDetails';
import { WaterWayDesignDetails } from '../Design/Waterway/WDTContainer/WDTContentComp/WaterwayDesign/DDetails';
//import { WaterWaySummaryDetails } from '../Design/Waterway/WDTContainer/WDTContentComp/Summary/SDetails'; 
import { WaterWayBermDetails } from '../Design/Waterway/WDTContainer/WDTContentComp/WaterwayInput/BermPage';
import { UgoMaterialList } from '../Design/UGO/UGOMaterialListPage/UGOMaterialPage';
import { UnderConstruction } from '../common/UnderConstruction';
import { AboutPage } from '../common/About';
import { UserIdentity } from 'cd-eft/security/UserIdentity';
import { MessageCenterComponent } from '../common/MessageCenter/MessageCenter';
import { ProjectContainer } from '../Projects/ProjectContainer/ProjectContainer';
import { EAuthLogin } from '../common/EAuthLogin';
import { GlossaryHelp } from '../common/GlossaryHelp/GlossaryHelpPage';
import { WaterwaySizingComp } from '../EFH2/Waterway Sizing Calulator/WaterwaySizingCalculator';
import { AdminErrorMessages } from '../adminPortal/AdminErrorMessages/AdminErrorMessages';
import { AdminContent } from '../adminPortal/AdminContent/AdminContent';
import { AdminProjectOwner } from '../adminPortal/AdminProjectOwner/AdminProjectOwner';
import { AdminReports } from '../adminPortal/AdminReports/AdminReports';
import { AdminGlobalPreferences } from '../adminPortal/AdminGlobalPreferences/AdminGlobalPreferences';
import { FormulaHome } from '../EFH2/Formulas/FormulasHome';
import { AdminErrorMessage } from '../adminPortal/AdminErrorMessage/AdminErrorMessage';
import { PopupContainer } from '../Design/common/SummaryDetails/SDetails';

// Styles
import '@arcgis/core/assets/esri/themes/light/main.css';
import 'nrcs-design-system/scss/nrcs-design-system.scss';
import 'uswds/dist/css/uswds.css';
import './app.scss';
import '../../assets/css/actionModal.scss';

// This is how to use USWDS components
import USWDS from 'uswds/src/js/components';
import { ClippedDEM } from '../EFH2/ClippedDEM/ClippedDem';
import { HydrologyCalcHome } from '../EFH2/HydrologyCalculator/HydrologyCalculator';

const { accordion } = USWDS;
const appConfig = stores.AppStore.appConfig;
const userProfileStore = stores.AppStore.userProfileStore;

let pageRenderCount = 0;

interface IAppComponentProps {
  appVm: AppVM;
}

const Page = (props) => {
  React.useEffect(() => {
    document.title = props.title || 'NRCS-NETS Application';
  }, [props.title]);
  return props.children;
};

const AppComponent = (props: IAppComponentProps) => {
  const vm = props.appVm;

  if (UserIdentity.accessToken && UserIdentity.userRoleId) {
    stores.AppStore.userProfileStore.userAvailable = true;
  } else {
    stores.AppStore.userProfileStore.userAvailable = false;
  }

  const userAvailable = stores.AppStore.userProfileStore.userAvailable;
  // Invalid authentication is made "true" when the Auth API returns a FAILURE status
  // If the user has logged out, never logged in, or Auth API returns SUCCESS, this is "false"
  const authError = stores.AppStore.userProfileStore.authError;

  React.useEffect(() => {
    // Only parse url if in FPAC
    // if (appConfig.useEAuth) vm.parseUrl();
    //not sure what this does....
    accordion.on();
  }, []);

  //defer full render until application state is ready.
  if (!vm.modelInitialized) {
    return null;
  }

  if (authError) {
    return <AuthErrorRouting />
  }
  else if (userAvailable) {
    return <ValidUserRouting userAvailable={userAvailable} />
  }
  else {
    return <InvalidUserRouting />
  }
};

const AuthErrorRouting = () => {
  return (
    <div className="eft-app">
      <Tophat />
      <BrowserRouter
        getUserConfirmation={() => {
          /* Empty callback to block the default browser prompt */
        }}
      >
        <Route
          path="/401"
          render={() => (
            <Page title="Error 401">
              <Error401 />
            </Page>
          )}
        />
        <Redirect to="/401" />
      </BrowserRouter>
    </div>
  )
}

const ValidUserRouting = (props) => {
  const userAvailable = props.userAvailable;
  return (<div className="eft-app">
    <Tophat />

    <BrowserRouter
      getUserConfirmation={() => {
        /* Empty callback to block the default browser prompt */
      }}
    >
      <div className="eft-app-content">
        <SideNav />

        <main role="main" tabIndex={-1} id='main'>
          {/* breadcrumbs go here i think */}
          {/* <Breadcrumbs /> */}

          <Switch>
            {appConfig.navigationFlags.redirectDesignToProjects ? (
              <Redirect exact from="/design" to="/projects" />
            ) : null}
            {appConfig.navigationFlags.redirectSingleWatershedToProjects ? (
              <Redirect exact from="/analysis/hydrology/single-watershed" to="/projects" />
            ) : null}
            {(!UserIdentity.userRoleId || !(UserIdentity.userRoleId === 4 || UserIdentity.userRoleId === 3)) ? (
              <Redirect from="/admin" to="/" />
            ) : null}
            <Route exact path="/">
              <Page title="NRCS-NETS Home">
                <Homepage />
              </Page>
            </Route>
            <Route path="/login">
              {userAvailable ? (
                <Redirect push to="/" />
              ) : (
                <Page title="NRCS-NETS Login3">{appConfig.useEAuth ? <EAuthLogin /> : <Login />}</Page>
              )}
            </Route>
            <Route path="/about">
              <Page title="NRCS-NETS About">
                <AboutPage />
              </Page>
            </Route>
            <Route path="/glossary">
              <Page title="NRCS-NETS Glossary and Help">
                <GlossaryHelp />
              </Page>
            </Route>
            <Route exact path="/ugomateriallist">
              <Page title="NRCS-NETS UGO Material List">
                <UgoMaterialList />
              </Page>
            </Route>
            <Route path="/training">
              <Page title="NRCS-NETS Training">
                {/* <UnderConstruction activeDev={true} /> */}
                <TrainingComp />
              </Page>
            </Route>
            <Route exact path="/analysis">
              <Page title="NRCS-NETS Analysis">
                <UnderConstruction />
              </Page>
            </Route>
            <Route exact path="/analysis/hydrology">
              <Page title="NRCS-NETS Hydrology">
                {appConfig.navigationFlags.enableHydrology ? <Intro /> : <UnderConstruction />}
              </Page>
            </Route>
            <Route path="/analysis/hydrology/single-watershed">
              <Page title="NRCS-NETS Watershed Analysis">
                {appConfig.navigationFlags.enableSingleWatershed ? <GISBox component="Analysis" /> : <UnderConstruction />}
              </Page>
            </Route>
            <Route path="/analysis/animal-waste">
              <Page title="NRCS-NETS Animal Waste">
                <UnderConstruction />
              </Page>
            </Route>
            <Route exact path="/analysis/waterway-sizing-calculator">
              <Page title="NRCS-NETS Waterway Sizing Calculator">
                {userProfileStore.applicationFlags.enablewaterwaysizing ? <WaterwaySizingComp mode='standalone' /> : <UnderConstruction activeDev={true} />}
              </Page>
            </Route>
            <Route path="/analysis/irrigation">
              <Page title="NRCS-NETS Irrigation">
                <UnderConstruction />
              </Page>
            </Route>
            <Route path="/analysis/hydraulic-formula">
              <Page title="NRCS-NETS Formulas">
                {stores.AppStore.userProfileStore.applicationFlags.enableformulas ? <FormulaHome /> : <UnderConstruction activeDev={true} />}
              </Page>
            </Route>
            <Route path="/analysis/clipped-dem">
              <Page title="NRCS-NETS Clipped DEM">
                {userProfileStore.applicationFlags.enableclippeddem ? <ClippedDEM /> : <UnderConstruction />}
              </Page>
            </Route>
            <Route path="/projects">
              <Page title="NRCS-NETS Project Library">
                {appConfig.navigationFlags.enableProjects ? <ProjectContainer /> : <UnderConstruction />}
              </Page>
            </Route>
            <Route exact path="/design/inputdetails">
              <Page title="NRCS-NETS Waterway Input Details">
                {userProfileStore.applicationFlags.enablewaterway ? <WaterWayInputDetails /> : <UnderConstruction activeDev={true} />}
              </Page>
            </Route>
            <Route exact path="/design/bermdiagram">
              <Page title="NRCS-NETS Waterway Berm Diagram Details">
                {userProfileStore.applicationFlags.enablewaterway ? <WaterWayBermDetails /> : <UnderConstruction activeDev={true} />}
              </Page>
            </Route>

            <Route exact path="/design/designdetails">
              <Page title="NRCS-NETS Waterway Design Details">
                {userProfileStore.applicationFlags.enablewaterway ? <WaterWayDesignDetails /> : <UnderConstruction activeDev={true} />}
              </Page>
            </Route>
            <Route exact path="/design/waterwaysummarydetails">
              <Page title="NRCS-NETS Waterway Summary Details">
                {userProfileStore.applicationFlags.enablewaterway ? PopupContainer(props = { design: 4 }) : <UnderConstruction activeDev={true} />}
              </Page>
            </Route>
            <Route exact path="/design/terracesummarydetails">
              <Page title="NRCS-NETS Terrace Summary Details">
                {userProfileStore.applicationFlags.enableterrace ? PopupContainer(props = { design: 3 }) : <UnderConstruction activeDev={true} />}
              </Page>
            </Route>
            <Route path="/design/:projectId?">
              <Page title="NRCS-NETS Design">
                <DesignContainer />
              </Page>
            </Route>

            {/* Admin interface */}
            <Route path="/admin" exact>
              <Page title="NRCS-NETS Admin : Content">
                {stores.AppStore.userProfileStore.applicationFlags.enableadmincontent ? <AdminContent /> : <UnderConstruction />}
              </Page>
            </Route>
            <Route path="/admin/error-messages/add-new" exact>
              <Page title="NRCS-NETS Admin : New Error Message">
                {stores.AppStore.userProfileStore.applicationFlags.enableadminerrormgmt ? <AdminErrorMessage /> : <UnderConstruction />}
              </Page>
            </Route>
            <Route path="/admin/error-messages/:messageId">
              {stores.AppStore.userProfileStore.applicationFlags.enableadminerrormgmt ? <AdminErrorMessage /> : <Page title="NRCS-NETS Admin : Error Message"><UnderConstruction /></Page>}
            </Route>
            <Route path="/admin/error-messages" exact>
              <Page title="NRCS-NETS Admin : Error Messages">
                {stores.AppStore.userProfileStore.applicationFlags.enableadminerrormgmt ? <AdminErrorMessages /> : <UnderConstruction />}
              </Page>
            </Route>
            <Route path="/admin/reports">
              <Page title="NRCS-NETS Admin : Reports">
                {stores.AppStore.userProfileStore.applicationFlags.enableadminreports ? <AdminReports /> : <UnderConstruction />}
              </Page>
            </Route>
            <Route exact path={["/admin/project-owner", "/admin/project-owner/:userId"]}>
              <Page title="NRCS-NETS Admin : Project Owner">
                {stores.AppStore.userProfileStore.applicationFlags.enableadminusermgmt ? <AdminProjectOwner /> : <UnderConstruction />}
              </Page>
            </Route>
            <Route path="/admin/global-preferences">
              <Page title="NRCS-NETS Admin : Global Preferences">
                {stores.AppStore.userProfileStore.applicationFlags.enableadminglobalpreference ? <AdminGlobalPreferences /> : <UnderConstruction />}
              </Page>
            </Route>

            <Route exact path="/analysis/hydrology/metadata">
              <Page title="NRCS-NETS Metadata">
                <Metadata />
              </Page>
            </Route>
            <Route exact path="/analysis/hydrology/hydrology-calculator">
              <Page title="Hydrology Calculator">
                {userProfileStore.applicationFlags.enablehydrocalc ? <HydrologyCalcHome /> : <UnderConstruction activeDev={true} />}
              </Page>
            </Route>
            <Route exact path="/analysis/waterway-sizing-calculator/details">
              <Page title="NRCS-NETS Waterway Sizing Calculator Details">
                {userProfileStore.applicationFlags.enablewaterwaysizing ? <WaterWaySizingCalculatorDetails /> : <UnderConstruction activeDev={true} />}
              </Page>
            </Route>
          </Switch>

        </main>

      </div>
    </BrowserRouter>

    <MessageCenterComponent />
  </div>)
}

const InvalidUserRouting = () => {
  return (<div className="eft-app">
    <Tophat />
    <BrowserRouter
      getUserConfirmation={() => {
        /* Empty callback to block the default browser prompt */
      }}
    >
      <Route path="/">
        <Page title="NRCS-NETS Login">{appConfig.useEAuth ? <EAuthLogin /> : <Login />}</Page>
      </Route>

      <Redirect to="/login" />
    </BrowserRouter>
  </div>)
}

export const App = observer(AppComponent);
